import { FC } from 'react';

import { Image } from '../../atoms';
import { messageCardProps, messageProps } from './MessageCard.types';
import { StyledSection, StyledIcon, ImageContainer } from './MessageCard.styles';
import { CollapsibleMessages } from './CollapsibleMessages';

import { createImageSizes } from '../../utils';
import { URL_CONSTANTS, constants } from '../../constants';
import { getWindowSession, transformResponse } from '../../utils';
import { SessionDataMap } from './MessageCard.schema';

const iconNameMap: Record<string, string> = {
  error: 'icon-warning',
  ocjError: 'icon-warning',
  info: 'icon-information',
  success: 'icon-success',
  alert: 'icon-alert',
  prefError: 'icon-warning',
  successMessage: 'icon-success',
  accessibilityAlerts: 'icon-accessibility',
  successMsg: 'icon-success',
  cancelSuccessMsg: 'icon-success',
  redemptionError: 'icon-alert',
  isAdultOnlyError: 'icon-alert',
  guestCountError: 'icon-alert',
  priceUpdateAlert: 'icon-warning',
  roomUpdateAlert: 'icon-warning',
  upcomingResAlert: 'icon-alert',
  fnaMessageBuyPoints: 'icon-alert',
  redemptionErrorMsg: 'icon-warning',
  restrictModification: 'icon-warning',
  creditCardAlert: 'icon-alert',
  creditCardAlertRRD: 'icon-alert',
  warning: 'icon-warning',
  availabilityError: 'icon-warning',
  cancelOTP: 'icon-warning',
  prearrivalVerification: 'icon-warning',
  foreignCurrencyAlert: 'icon-warning',
  currencyConversionAlert: 'icon-warning',
  prepayRateAlert: 'icon-information',
  acrsMemberBookingAlert: 'icon-warning',
  qrvVerificationSuccess: 'icon-success',
  priceChangeAlert: 'icon-information',
  unAvailableRoomAlert: 'icon-warning',
  fnaMessage: 'icon-elite-status',
  redemptionPanelError: 'icon-warning',
};
const classNameMap: Record<string, string> = {
  error: 'error',
  redemptionPanelError: 'redemption-panel-error',
  fnaMessage: 'fna-message',
  ocjError: 'ocj-error',
  info: 'information',
  success: 'success',
  alert: 'alert',
  alertError: 'alert-error',
  hotelmessage: 'hotel-message',
  hotelmessagemgp: 'hotel-message-mgp',
  prefError: 'pref-error',
  successMessage: 'success-message',
  accessibilityAlerts: 'accessibility-alert',
  graphQlError: 'alert-error mt-5 mb-0',
  successMsg: 'success-msg',
  cancelSuccessMsg: 'cancel-success-msg',
  failureMessage: 'failure-message',
  redemptionError: 'no-redemption-error',
  isAdultOnlyError: 'is-adult-error',
  guestCountError: 'is-adult-error',
  priceUpdateAlert: 'warning-msg',
  roomUpdateAlert: 'warning-msg',
  upcomingResAlert: 'alert upcomingres-alert',
  fnaMessageBuyPoints: 'fna-message-alert',
  redemptionErrorMsg: 'redemption-error-msg',
  restrictModification: 'modify-restrict',
  creditCardAlert: 'alert credit-card-alert mt-4 mb-5',
  creditCardAlertRRD: 'alert credit-card-alert mb-5',
  warning: 'warning-msg',
  availabilityError: 'warning-msg',
  cancelOTP: 'error cancel-otp',
  prearrivalVerification: 'prearrival-verification',
  foreignCurrencyAlert: 'foreign-currency-alert',
  currencyConversionAlert: 'foreign-currency-alert',
  prepayRateAlert: 'alert prepay-rate-alert',
  acrsMemberBookingAlert: 'acrs-member-booking-alert',
  qrvVerificationSuccess: 'success-message',
  priceChangeAlert: 'mb-3 mt-3 price-change-alert',
  unAvailableRoomAlert: 'mb-1 mt-3 p-3 error unavailable-room-alert',
};

export const MessageCard: FC<messageCardProps> = ({
  type,
  messages,
  showCard,
  heading,
  fileReferenceDynamic,
  hideIcon,
  isLinkRequired = false,
  linkTitle = '',
  showModal = false,
  ariaLabel,
  setOpenModal,
  ariaLive,
  role,
  pageType,
  collapsible = false,
  moreLabel,
  lessLabel,
  maxCollapsedLines = 3,
}: messageCardProps) => {
  const iconName = iconNameMap[type] || 'icon-information';
  const { RLM_PAGE, SONDAR_BRAND_CODE } = constants;

  const sessionObject = getWindowSession();
  const { brandCode } = transformResponse(sessionObject, SessionDataMap);

  const isSondar = pageType === RLM_PAGE && brandCode === SONDAR_BRAND_CODE;
  const className = isSondar ? `${classNameMap[type]} col-lg-7 mt-lg-2` : classNameMap[type];
  const renditions = fileReferenceDynamic?.renditions;
  const imageSize = createImageSizes(renditions);

  return showCard ? (
    <StyledSection
      data-component-name="m-book-MessageCard"
      data-testid="book-MessageCard"
      id={type}
      className={className}
      {...(ariaLive && { 'aria-live': ariaLive })}
      {...(role && { role: role })}
    >
      {fileReferenceDynamic?.damPath && (
        <ImageContainer>
          <Image
            url={fileReferenceDynamic?.damPath}
            renditions={fileReferenceDynamic?.renditions}
            dynamic={fileReferenceDynamic?.dynamic}
            size={imageSize}
            appliedClass={'image-view'}
          />
        </ImageContainer>
      )}
      {!hideIcon && <StyledIcon className={[iconName, 'hide-icon'].join(' ')} />}
      <span>
        {heading && <span className="messagecard-heading">{heading}</span>}
        <CollapsibleMessages
          collapsible={collapsible}
          moreLabel={moreLabel}
          lessLabel={lessLabel}
          maxCollapsedLines={maxCollapsedLines}
        >
          {messages.map((item: messageProps, index) => {
            const keyBase = `${index}-messageCard`;
            // RETURN COMPLETE COMPONENT AS A MESSAGE
            if ('component' in item && item.component) {
              return (
                <div className="message" key={`${keyBase}-component`}>
                  {item.component}
                </div>
              );
            }
            // RETURN RICH/TREATED-AS-HTML TEXT MESSAGES (error, ocjError, or isRichText)
            const { text, isRichText } = item as Extract<messageProps, { text: string }>;
            if (type === 'error' || type === 'ocjError' || isRichText || type === 'redemptionPanelError') {
              return (
                <div
                  key={index.toString() + 'messageCardRichText'}
                  data-rte-editelement
                  className="message"
                  dangerouslySetInnerHTML={{ __html: text }}
                />
              );
            }
            //RETURN PLAIN TEXT MESSAGES (with optional link)
            return (
              <p className="message" key={index.toString() + 'messageCard'}>
                {text}
                {isLinkRequired &&
                  (showModal ? (
                    setOpenModal && (
                      <a href="#" className="linktitle ml-1 t-font-weight-r" onClick={() => setOpenModal(true)}>
                        {linkTitle}
                      </a>
                    )
                  ) : (
                    <a
                      href={URL_CONSTANTS.TI_REDIRECT_STATIC_LINK}
                      className="standard d-inline-block"
                      target="_blank"
                      rel="noreferrer"
                      aria-label={ariaLabel?.replace('{XX}', linkTitle)}
                    >
                      <span className="linktitle">{linkTitle}</span>
                      <span className="icon-external-arrow"></span>
                    </a>
                  ))}
              </p>
            );
          })}
        </CollapsibleMessages>
      </span>
    </StyledSection>
  ) : null;
};
